import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import { alpha } from '@mui/material/styles';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import CloseIcon from '@mui/icons-material/Close';

import JsonEditor, { IJsonEditorHandle } from './JsonEditor';
import { download } from '../common/download';
import { classifyScript, formatJson, IScriptFile, IScriptFileError, readScriptFiles, SCRIPT_TITLE, TScriptKind } from './scriptFiles';

/**
 * The ucon + StyleScript editors of a Szkriptek tab (admin drawer, AI designer page), with file
 * loading: picked, dropped anywhere on the tab or pasted (Ctrl+V outside the editors), each .json
 * goes to the editor its content belongs to (scriptFiles.classifyScript) and is applied at once
 * through `onLoad`, both scripts in one call when both came, so the host can send the ucon first.
 *
 * Every change made here (apply, file, paste, restore) first keeps the pair it replaces, for
 * "Korábbi állapotok". While the tab is mounted, a file dropped elsewhere on the page is refused,
 * so a miss does not open the JSON in place of the page. What a slot applies to, and what else it
 * offers (saving, override reset), is the host's.
 */

export interface IScriptSlot {
    value: unknown;
    applyLabel: string;
    dirtyNote: string;
    onApply: (parsed: any) => void;
    /** "Letöltés" (and Ctrl+S in the editor) saves the editor's text under this name */
    fileName: string;
    /** between the title and the editor: what is loaded, notes */
    header?: React.ReactNode;
    renderExtraActions?: (text: string, valid: boolean) => React.ReactNode;
}

/** Scripts from files, already checked: `style` is an array, `ucon` an object. */
export interface ILoadedScripts { ucon?: any; style?: any[]; }

/** Files that are not scripts but that the host takes (e.g. a room scan). */
export interface IOtherFiles {
    /** for the file picker's accept */
    accept: string;
    test: (file: File) => boolean;
    /** what they are, for the hints */
    label: string;
    onFiles: (files: File[]) => void;
}

interface IScriptsEditorProps {
    intro?: React.ReactNode;
    ucon: IScriptSlot;
    style: IScriptSlot;
    onLoad: (scripts: ILoadedScripts) => void;
    /** "Pár letöltése": both editors as one `{ ucon, style }` file, which the loader reads back */
    pairFileName: string;
    /** "Termék szkriptjei": back to the product's own pair */
    onRestoreProduct?: () => void;
    otherFiles?: IOtherFiles;
    /** the host's last result (apply, save), in the status bar */
    status: string | null;
    children?: React.ReactNode;
}

interface ISnapshot { key: number; at: Date; before: string; ucon: unknown; style: unknown; }

const KINDS: TScriptKind[] = ['ucon', 'style'];
const MAX_SNAPSHOTS = 10;
const PASTE_NAME = 'vágólap';

const hasFiles = (e: { dataTransfer: DataTransfer | null }) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');

let nextSnapshot = 1;

const ScriptsEditor: React.FC<IScriptsEditorProps> = ({
    intro, ucon, style, onLoad, pairFileName, onRestoreProduct, otherFiles, status, children,
}) => {
    const slots: Record<TScriptKind, IScriptSlot> = { ucon, style };
    const editors: Record<TScriptKind, React.RefObject<IJsonEditorHandle | null>> = {
        ucon: React.useRef<IJsonEditorHandle>(null),
        style: React.useRef<IJsonEditorHandle>(null),
    };
    const input = React.useRef<HTMLInputElement>(null);
    // dragenter/dragleave fire for every child crossed; the overlay goes when the count is back to 0
    const depth = React.useRef(0);
    const [dragging, setDragging] = React.useState(false);
    const [report, setReport] = React.useState<{ text: string; error: boolean } | null>(null);
    const [snapshots, setSnapshots] = React.useState<ISnapshot[]>([]);
    const [historyAnchor, setHistoryAnchor] = React.useState<HTMLElement | null>(null);
    // the × on the status (left: the page's floating button covers the drawer's bottom-right); a new message shows again
    const [statusClosed, setStatusClosed] = React.useState(false);
    React.useEffect(() => { setStatusClosed(false); }, [status, report]);

    // A file dropped outside a drop target (the 3D view, the page) would replace the page with it.
    React.useEffect(() => {
        const refuse = (e: DragEvent) => {
            if (!hasFiles(e) || e.defaultPrevented) return;
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
        };
        window.addEventListener('dragover', refuse);
        window.addEventListener('drop', refuse);
        return () => {
            window.removeEventListener('dragover', refuse);
            window.removeEventListener('drop', refuse);
        };
    }, []);

    /** Keeps the pair the Designer shows now, before `what` replaces it. */
    const remember = (what: string) => {
        const snapshot = { key: nextSnapshot++, at: new Date(), before: what, ucon: ucon.value, style: style.value };
        setSnapshots((list) => {
            const last = list[0];
            if (last && JSON.stringify([last.ucon, last.style]) === JSON.stringify([snapshot.ucon, snapshot.style])) {
                return list;
            }
            return [snapshot, ...list].slice(0, MAX_SNAPSHOTS);
        });
    };

    const applyLoaded = (scripts: ILoadedScripts, what: string) => {
        remember(what);
        onLoad(scripts);
    };

    /** Puts the scripts found in their editors and applies them; `errors` are files that held none. */
    const loadScripts = (scripts: IScriptFile[], errors: IScriptFileError[], extra: string[] = []) => {
        // One editor per kind: of several files of a kind the last one wins.
        const picked: Partial<Record<TScriptKind, IScriptFile>> = {};
        const skipped = errors.map(({ name, error }) => `${name} (${error})`);
        for (const script of scripts) {
            const earlier = picked[script.kind];
            if (earlier) skipped.push(`${earlier.name} (${SCRIPT_TITLE[script.kind]}: ${script.name} felülírta)`);
            picked[script.kind] = script;
        }
        const loaded = KINDS.filter((kind) => picked[kind]);
        loaded.forEach((kind) => editors[kind].current?.load(formatJson(picked[kind]!.value)));

        const parts = [
            loaded.length ? `Betöltve: ${loaded.map((kind) => `${picked[kind]!.name} → ${SCRIPT_TITLE[kind]}`).join(', ')}.` : '',
            ...extra,
            skipped.length ? `Kihagyva: ${skipped.join('; ')}.` : '',
        ];
        setReport({ text: parts.filter(Boolean).join(' '), error: !loaded.length && !extra.length });
        if (loaded.length) {
            const names = Array.from(new Set(loaded.map((kind) => picked[kind]!.name)));
            applyLoaded({ ucon: picked.ucon?.value, style: picked.style?.value as any[] | undefined },
                names[0] === PASTE_NAME ? 'beillesztés' : `fájl: ${names.join(', ')}`);
        }
    };

    const load = async (files: File[]) => {
        if (!files.length) return;
        const others = otherFiles ? files.filter(otherFiles.test) : [];
        const extra: string[] = [];
        if (others.length) {
            remember(`${otherFiles!.label}: ${others.map((f) => f.name).join(', ')}`);
            otherFiles!.onFiles(others);
            extra.push(`${otherFiles!.label}: ${others.map((f) => f.name).join(', ')}.`);
        }
        const { scripts, errors } = await readScriptFiles(files.filter((f) => !others.includes(f)));
        loadScripts(scripts, errors, extra);
    };

    const drop = {
        onDragEnter: (e: React.DragEvent) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            depth.current += 1;
            setDragging(true);
        },
        onDragOver: (e: React.DragEvent) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: (e: React.DragEvent) => {
            if (!hasFiles(e)) return;
            depth.current = Math.max(0, depth.current - 1);
            if (depth.current === 0) setDragging(false);
        },
        onDrop: (e: React.DragEvent) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            depth.current = 0;
            setDragging(false);
            load(Array.from(e.dataTransfer.files));
        },
    };

    // Inside an editor or a field, Ctrl+V is an ordinary paste.
    const handlePaste = (e: React.ClipboardEvent) => {
        if ((e.target as HTMLElement).closest('.cm-editor, input, textarea')) return;
        e.preventDefault();
        if (e.clipboardData.files.length) {
            load(Array.from(e.clipboardData.files));
            return;
        }
        const text = e.clipboardData.getData('text');
        if (!text.trim()) return;
        try {
            loadScripts(classifyScript(PASTE_NAME, text), []);
        } catch (error) {
            loadScripts([], [{ name: PASTE_NAME, error: error instanceof Error ? error.message : String(error) }]);
        }
    };

    const downloadPair = () => {
        const pair: Record<TScriptKind, unknown> = { ucon: null, style: null };
        for (const kind of KINDS) {
            const text = editors[kind].current?.text() ?? '';
            if (!text.trim()) continue;
            try {
                pair[kind] = JSON.parse(text);
            } catch {
                setReport({ text: `A pár nem tölthető le: a ${SCRIPT_TITLE[kind]} nem érvényes JSON.`, error: true });
                return;
            }
        }
        download(pairFileName, formatJson(pair));
    };

    const restore = (snapshot: ISnapshot) => {
        setHistoryAnchor(null);
        applyLoaded({
            ...(snapshot.ucon != null ? { ucon: snapshot.ucon } : {}),
            ...(Array.isArray(snapshot.style) ? { style: snapshot.style } : {}),
        }, `visszaállítás (${snapshot.at.toLocaleTimeString()})`);
        setReport({ text: `Visszaállítva a ${snapshot.at.toLocaleTimeString()}-kori állapot (${snapshot.before} előtti).`, error: false });
    };

    const restoreProduct = () => {
        remember('a termék szkriptjei');
        onRestoreProduct!();
        setReport({ text: 'Visszaállítva a termék saját szkriptjeire.', error: false });
    };

    const section = (kind: TScriptKind) => {
        const slot = slots[kind];
        const save = (text: string, valid: boolean) => { if (valid && text.trim()) download(slot.fileName, text); };
        return (
            <Box key={kind} sx={{ mt: kind === 'style' ? 2 : 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{SCRIPT_TITLE[kind]}</Typography>
                {slot.header}
                <JsonEditor
                    ref={editors[kind]}
                    value={slot.value}
                    applyLabel={slot.applyLabel}
                    dirtyNote={slot.dirtyNote}
                    onApply={(parsed) => { remember(`${SCRIPT_TITLE[kind]} alkalmazása`); slot.onApply(parsed); }}
                    onSaveKey={save}
                    renderExtraActions={(text, valid) => (
                        <>
                            {slot.renderExtraActions?.(text, valid)}
                            <Button variant="outlined" size="small" disabled={!valid || !text.trim()} title="Ctrl+S"
                                onClick={() => save(text, valid)}>
                                Letöltés
                            </Button>
                        </>
                    )}
                />
            </Box>
        );
    };

    return (
        // Toolbar on top, editors scrolling in between, status under them: nothing scrolls behind the status.
        // Fills the host's box (height: 100%); without a definite height it grows and the host scrolls.
        <Box {...drop} tabIndex={-1} onPaste={handlePaste}
            sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', outline: 'none' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5, flexShrink: 0 }}>
                <Button size="small" variant="outlined" startIcon={<FolderOpenIcon />} onClick={() => input.current?.click()}>
                    Fájlok megnyitása…
                </Button>
                <Button size="small" startIcon={<DownloadIcon />} onClick={downloadPair}
                    title="Mindkét szkript egy fájlban; visszatölthető">
                    Pár letöltése
                </Button>
                <Button size="small" startIcon={<HistoryIcon />} disabled={!snapshots.length}
                    onClick={(e) => setHistoryAnchor(e.currentTarget)}>
                    Korábbi állapotok{snapshots.length ? ` (${snapshots.length})` : ''}
                </Button>
                {onRestoreProduct && (
                    <Button size="small" startIcon={<RestoreIcon />} onClick={restoreProduct}>
                        Termék szkriptjei
                    </Button>
                )}
                <input ref={input} type="file" multiple hidden
                    accept={`.json,application/json${otherFiles ? `,${otherFiles.accept}` : ''}`}
                    onChange={(e) => { load(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Fájlok ide húzva vagy Ctrl+V-vel (a szerkesztőkön kívül) is betölthetők
                    {otherFiles ? `; ${otherFiles.label} is` : ''}. A tartalom dönti el, UCon vagy stílus szkript, és
                    betöltés után azonnal alkalmazódik. Szerkesztőben: Ctrl+Enter alkalmaz, Ctrl+S letölt, Ctrl+F keres.
                </Typography>

                <Menu anchorEl={historyAnchor} open={!!historyAnchor} onClose={() => setHistoryAnchor(null)}>
                    {snapshots.map((snapshot) => (
                        <MenuItem key={snapshot.key} dense onClick={() => restore(snapshot)}>
                            <ListItemText primary={snapshot.at.toLocaleTimeString()} secondary={`${snapshot.before} előtt`} />
                        </MenuItem>
                    ))}
                </Menu>

                {intro}
                {KINDS.map(section)}
                {children}
            </Box>

            {(status || report) && !statusClosed && (
                <Stack direction="row" alignItems="flex-start" spacing={0.5}
                    sx={{ flexShrink: 0, mt: 1, pt: 0.5, borderTop: 1, borderColor: 'divider', maxHeight: 88, overflow: 'auto' }}>
                    <IconButton size="small" aria-label="üzenet bezárása" onClick={() => setStatusClosed(true)}
                        sx={{ p: 0.25, color: 'text.secondary' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {report && (
                            <Typography variant="caption" color={report.error ? 'error' : 'text.secondary'} sx={{ display: 'block' }}>
                                {report.text}
                            </Typography>
                        )}
                        {status && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {status}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            )}

            {dragging && (
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', borderRadius: 1,
                    border: 2, borderStyle: 'dashed', borderColor: 'primary.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }}>
                    <Stack alignItems="center" spacing={1} sx={{ px: 3, py: 2,
                        width: 'fit-content', maxWidth: '90%', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3 }}>
                        <UploadFileIcon color="primary" sx={{ fontSize: 40 }} />
                        <Typography variant="body2" color="primary" align="center">
                            Engedje el: UCon és/vagy stílus szkript (.json){otherFiles ? `, ${otherFiles.label}` : ''} — betöltés és alkalmazás
                        </Typography>
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default ScriptsEditor;
